import type { TReminderFormValues } from './schema';
import { useFormikContext, ErrorMessage } from 'formik';
import { useEffect, useMemo } from 'react';
import { InfoCircle } from 'iconsax-react';

import { Alert, AlertDescription } from '@src/design-system/alert';
import { CustomErrorMessage } from '@src/design-system/error-message';
import { cn } from '@design-system/helpers';
import { SelectField } from '@src/shared/fields/select-field';
import { RuleSelect } from './rule-select';
import { REMINDER_TRIGGER_TYPES } from '@src/shared/types/reminder';
import { formatTextForDisplay } from '@src/shared/utils/helpers/format-text-for-display';
import { Textfield } from '@src/shared/fields/text-field';
import { useParams } from 'react-router-dom';
import ruleApi from '@src/features/programs/pages/create/services/rule-api';
import { RULE_TYPES } from '@src/shared/types/rule';

export const DateRuleFields = () => {
  const { id: programId = '' } = useParams();

  const { data: rulesData } = ruleApi.useFetch({
    program: programId,
    type: [RULE_TYPES.date],
  });
  const rules = rulesData?.results || [];

  const { values, setFieldValue, setFieldTouched, setTouched } =
    useFormikContext<TReminderFormValues>();

  const frequency =
    values.frequency == null || String(values.frequency) === ''
      ? 0
      : Number(values.frequency);
  const selectedRule = rules.find(
    (rule) => String(rule.id) === String(values.rule)
  );
  const ruleName = selectedRule?.name || 'Rule Date';

  useEffect(() => {
    if (!values.trigger_type) {
      setFieldValue('trigger_type', REMINDER_TRIGGER_TYPES.before, false);
    }
  }, [setFieldValue, values.trigger_type]);

  const handleFrequencyBlur = () => {
    setFieldTouched('frequency', true);
    if (values.frequency == null || String(values.frequency) === '') {
      setFieldValue('frequency', 0, false);
    }
  };

  const alertMessage = useMemo(() => {
    if (values.trigger_type === REMINDER_TRIGGER_TYPES.on) {
      return (
        <>
          Send this reminder on the{' '}
          <span className='font-bold'>{ruleName}</span>.
        </>
      );
    }

    if (values.trigger_type === REMINDER_TRIGGER_TYPES.after) {
      return (
        <>
          Send this reminder after{' '}
          <span className='font-bold'>{frequency}</span> days of the{' '}
          <span className='font-bold'>{ruleName}</span>.
        </>
      );
    }

    return (
      <>
        Send this reminder before <span className='font-bold'>{frequency}</span>{' '}
        days of the <span className='font-bold'>{ruleName}</span>.
      </>
    );
  }, [frequency, ruleName, values.trigger_type]);

  const shouldShowAlert = Boolean(values.trigger_type);

  return (
    <div className='flex flex-col gap-4'>
      <div className='grid grid-cols-4 gap-4'>
        <div className='col-span-1'>
          <SelectField
            selectName='trigger_type'
            selectPlaceholder='Select'
            onValueChange={(value) => {
              setTouched({ frequency: false });
              setFieldValue('frequency', 0);
              setFieldValue('trigger_type', value);
            }}
            options={Object.values(REMINDER_TRIGGER_TYPES).map((t) => ({
              label: formatTextForDisplay(t),
              value: t,
            }))}
          />
        </div>

        <div className='col-span-2'>
          <RuleSelect
            rules={rules}
            placeholder='Rule'
          />
        </div>

        <div className='col-span-1 overflow-visible'>
          <div className='flex items-start'>
            <div className='w-[60px] shrink-0'>
              <Textfield
                className='rounded-r-none'
                type='number'
                disabled={values.trigger_type === REMINDER_TRIGGER_TYPES.on}
                name='frequency'
                hideError
                onBlur={handleFrequencyBlur}
              />
            </div>
            <div
              className={cn(
                'flex h-12 shrink-0 items-center justify-center bg-primary-100 text-gray-800 text-sm font-medium rounded-r-lg px-3',
                values.trigger_type === REMINDER_TRIGGER_TYPES.on &&
                  'opacity-70'
              )}
            >
              Days
            </div>
          </div>
          <ErrorMessage
            name='frequency'
            component={CustomErrorMessage}
            className='whitespace-nowrap'
          />
        </div>
      </div>

      {shouldShowAlert ? (
        <Alert className='border-0 bg-primary-100 text-primary-1000'>
          <AlertDescription className='text-primary-1000 flex items-center gap-1 text-sm'>
            <span>
              <InfoCircle size={20} />
            </span>
            {alertMessage}
          </AlertDescription>
        </Alert>
      ) : null}
    </div>
  );
};
